import { useLanguage } from '../context/LanguageContext';
import { useUsage } from '../context/UsageContext';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertCircle, ArrowUpCircle, Crown, Zap } from 'lucide-react';

const UNLIMITED = -1;

const PLAN_LABELS = {
  starter: { es: 'Inicial', en: 'Starter', icon: Zap, color: 'text-muted-foreground' },
  professional: { es: 'Profesional', en: 'Professional', icon: Crown, color: 'text-bronze' },
  enterprise: { es: 'Enterprise', en: 'Enterprise', icon: Crown, color: 'text-emerald' },
};

/**
 * Shows current plan + usage of key limits. Call it from the Dashboard
 * and from Settings. Also used by <PlanLimitBanner /> to detect near-limit
 * states.
 */
export default function PlanLimitsCard() {
  const { language } = useLanguage();
  const { data } = useUsage();

  if (!data) return null;

  const { plan, limits, usage } = data;
  const planInfo = PLAN_LABELS[plan] || PLAN_LABELS.starter;
  const PlanIcon = planInfo.icon;

  const rows = [
    {
      label: language === 'es' ? 'Usuarios' : 'Users',
      used: usage.users,
      limit: limits.max_users,
    },
    {
      label: language === 'es' ? 'Casos activos' : 'Active cases',
      used: usage.active_cases,
      limit: limits.max_active_cases,
    },
    {
      label: language === 'es' ? 'Consultas IA este mes' : 'AI queries this month',
      used: usage.ai_queries_this_month,
      limit: limits.ai_queries_per_month,
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <PlanIcon className={`w-5 h-5 ${planInfo.color}`} />
          <CardTitle className="text-base font-semibold">
            {language === 'es' ? 'Plan' : 'Plan'}{' '}
            <span className={planInfo.color}>{planInfo[language]}</span>
          </CardTitle>
        </div>
        {plan !== 'enterprise' && (
          <Button variant="outline" size="sm" className="gap-1">
            <ArrowUpCircle className="w-4 h-4" />
            {language === 'es' ? 'Actualizar' : 'Upgrade'}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.map((r) => (
          <UsageBar key={r.label} label={r.label} used={r.used} limit={r.limit} language={language} />
        ))}
      </CardContent>
    </Card>
  );
}

function UsageBar({ label, used, limit, language }) {
  const isUnlimited = limit === UNLIMITED;
  const percent = isUnlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const nearLimit = !isUnlimited && percent >= 80;
  const atLimit = !isUnlimited && percent >= 100;

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-muted-foreground">{label}</span>
        <span
          className={`font-medium ${
            atLimit ? 'text-destructive' : nearLimit ? 'text-bronze' : 'text-foreground'
          }`}
        >
          {used}
          {isUnlimited ? ` / ${language === 'es' ? 'Ilimitado' : 'Unlimited'}` : ` / ${limit}`}
        </span>
      </div>
      {!isUnlimited && (
        <Progress
          value={percent}
          className={atLimit ? '[&>div]:bg-destructive' : nearLimit ? '[&>div]:bg-bronze' : ''}
        />
      )}
    </div>
  );
}

/**
 * Standalone banner that appears when ANY resource is at or near its limit.
 * Useful at the top of the Dashboard.
 */
export function PlanLimitBanner() {
  const { language } = useLanguage();
  const { data } = useUsage();

  if (!data || data.plan === 'enterprise') return null;

  const { limits, usage } = data;
  const blocked = [];
  const check = (key, used, label) => {
    const limit = limits[key];
    if (limit === UNLIMITED || used < limit) return;
    blocked.push(label);
  };

  check('max_users', usage.users, language === 'es' ? 'usuarios' : 'users');
  check('max_active_cases', usage.active_cases, language === 'es' ? 'casos activos' : 'active cases');
  check('ai_queries_per_month', usage.ai_queries_this_month, language === 'es' ? 'consultas IA' : 'AI queries');

  if (blocked.length === 0) return null;

  return (
    <div className="mb-6 p-4 rounded-md border border-bronze/30 bg-bronze/5 flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-bronze shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-medium text-foreground">
          {language === 'es'
            ? `Ha alcanzado el limite de ${blocked.join(', ')} en su plan actual.`
            : `You have reached the limit for ${blocked.join(', ')} on your current plan.`}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {language === 'es'
            ? 'Actualice su plan para continuar agregando recursos.'
            : 'Upgrade your plan to continue adding resources.'}
        </p>
      </div>
      <Button variant="outline" size="sm" className="gap-1 shrink-0">
        <ArrowUpCircle className="w-4 h-4" />
        {language === 'es' ? 'Actualizar plan' : 'Upgrade plan'}
      </Button>
    </div>
  );
}

/**
 * Standard treatment for a 402 Payment Required response from the backend.
 * Use in catch blocks of create operations.
 */
export function handlePlanLimitError(error, language = 'es') {
  if (error?.status === 402 || (error?.message || '').toLowerCase().includes('limit reached')) {
    return language === 'es'
      ? 'Ha alcanzado el limite de su plan. Actualice para continuar.'
      : 'You have reached your plan limit. Upgrade to continue.';
  }
  return error?.message || (language === 'es' ? 'Error desconocido' : 'Unknown error');
}
